import React from 'react';
import styled from 'styled-components';

import { ImageBlockData } from 'api/cms';
import Box from 'components/Box';
import { FilestackImage, Image } from 'components/Image';
import { Body, BodySmall, RichTextHeadline } from 'components/Text';
import { TrackedConditionalLink } from 'components/TrackedLink';
import { colors } from 'theme';
import { imageUrl } from 'utils/filestack';

const GifImage = styled(Image)<{ $fullWidthLayout?: boolean }>`
  max-height: 500px;
  max-width: 100%;
  width: auto;
  ${({ $fullWidthLayout }) =>
    $fullWidthLayout &&
    `
    display: block;
    margin: 0 auto;
    height: auto;
    width: auto;
  `}
  @supports (object-fit: contain) {
    object-fit: contain;
    width: 100%;
    ${({ $fullWidthLayout }) =>
      !$fullWidthLayout &&
      `
      object-position: 0 50%;
    `}
  }
`;
const ImageContainer = styled.div<{ keepDimensions?: boolean }>`
  img {
    display: block;
    margin: 0 auto;
    max-width: 100%;
    width: ${({ keepDimensions }) => (keepDimensions ? 'auto' : '100%')};
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const ImageBlock = ({
  data: { headline, subtitle, image, url, keepDimensions },
  className,
  fullWidthLayout,
  blockIndex,
  pageUpdated,
}: {
  data: ImageBlockData;
  className?: string;
  fullWidthLayout?: boolean;
  blockIndex: number;
  pageUpdated: string;
}) => {
  const { caption, altText } = image;
  const isGif = image.mimetype === 'image/gif';
  return (
    <Box
      as="figure"
      m={0}
      w={1}
      fullWidthLayout={fullWidthLayout}
      className={className}
      data-qa-id="cms-image-block"
      data-qa-keep-dimensions={!!keepDimensions}
    >
      {(headline || subtitle) && (
        <Box as="figcaption" mb={48}>
          {headline && (
            <RichTextHeadline textAlign={fullWidthLayout ? 'center' : 'left'}>
              {headline}
            </RichTextHeadline>
          )}
          {subtitle && (
            <Body
              analyticsData={{
                eventName: 'CMS Block',
                eventProperties: {
                  block_position: blockIndex,
                  block_type: 'Image',
                },
              }}
              textAlign={fullWidthLayout ? 'center' : 'left'}
              dangerouslySetInnerHTML={{
                __html: subtitle,
              }}
            />
          )}
        </Box>
      )}
      <Box as={headline || subtitle ? 'figure' : 'div'} m={0}>
        {/* @ts-expect-error ts-migrate(2786) FIXME: 'TrackedConditionalLink' cannot be used as a JSX c... Remove this comment to see the full error message */}
        <TrackedConditionalLink
          link={url}
          eventName="CMS Block"
          eventProperties={{
            block_position: blockIndex,
            block_type: 'Image',
            cta_type: 'Image',
            page_updated: pageUpdated,
          }}
          aria-label={altText || getText('Follow image link')}
        >
          {isGif ? (
            <GifImage
              src={imageUrl({
                handle: image.handle,
                mimetype: image.mimetype,
              })}
              alt={altText || ''}
              $fullWidthLayout={fullWidthLayout}
              webp
            />
          ) : (
            <ImageContainer keepDimensions={keepDimensions}>
              <FilestackImage
                data-qa-id="cms-image-block-image"
                centered
                data={image}
                alt={altText || ''}
                width={keepDimensions ? undefined : 1152}
                quality={image.mimetype === 'image/png' ? 90 : 70}
              />
            </ImageContainer>
          )}
        </TrackedConditionalLink>
        {caption && (
          <BodySmall color={colors.battleshipGrey} mt="8px" as="figcaption">
            {caption}
          </BodySmall>
        )}
      </Box>
    </Box>
  );
};

// eslint-disable-next-line import/no-default-export
export default ImageBlock;
